/** @velyn-custom BEGIN — vely-hero-slideshow.js (Swiper + dock nav + progress; pairs with sections/vely-hero-slideshow.liquid) */
(function () {
  const getSwiper = () => window.Swiper;
  const MOBILE_MAX = 1024.98;

  class VelyHeroSlideshow extends HTMLElement {
    connectedCallback() {
      if (this._velyHeroInitStarted) return;
      this._velyHeroInitStarted = true;
      this._onDockClick = this._onDockClick.bind(this);
      this._onPlayClick = this._onPlayClick.bind(this);
      this._onResize = this._onResize.bind(this);
      this._dockHolder = this.querySelector(".vely-hero-slideshow__dock-holder");
      this._playBtn = this.querySelector(".vely-hero-slideshow__play-btn");
      this._paginationEl = this.querySelector(
        ".vely-hero-slideshow__pagination-pill",
      );
      this._dockHolder?.addEventListener("click", this._onDockClick);
      this._playBtn?.addEventListener("click", this._onPlayClick);
      window.addEventListener("resize", this._onResize);
      this.waitForDeps(() => this.initSlideShow());
    }

    disconnectedCallback() {
      this._dockHolder?.removeEventListener("click", this._onDockClick);
      this._playBtn?.removeEventListener("click", this._onPlayClick);
      window.removeEventListener("resize", this._onResize);
      this.clearAutoAdvance();
      if (this.mainSlide && this._onPointerEnter) {
        this.mainSlide.removeEventListener("pointerenter", this._onPointerEnter);
        this.mainSlide.removeEventListener("pointerleave", this._onPointerLeave);
      }
      if (this._swiper) {
        this._swiper.destroy(true, true);
        this._swiper = null;
      }
      this._velyHeroInitStarted = false;
      this.classList.remove("vely-hero-slideshow--ready");
    }

    waitForDeps(callback, attempt = 0) {
      const hasSwiper = typeof getSwiper() === "function";
      const hasPagination = Boolean(window.VelySwiperPagination?.setActiveBullet);
      if (hasSwiper && hasPagination) {
        callback();
        return;
      }
      if (attempt > 160) {
        console.warn(
          "[vely-hero-slideshow] Swiper or VelySwiperPagination not available",
        );
        return;
      }
      setTimeout(() => this.waitForDeps(callback, attempt + 1), 50);
    }

    _onResize() {
      if (!this._swiper || this._swiper.destroyed) return;
      if (this.isMobileViewport()) {
        this.refreshMobilePagination(this._swiper);
      }
      this.syncVideoPlayButton(this._swiper);
    }

    isMobileViewport() {
      return window.innerWidth <= MOBILE_MAX;
    }

    getVideoPlayLabel() {
      return this.dataset.playLabel || "";
    }

    getVideoPauseLabel() {
      return this.dataset.pauseLabel || "";
    }

    initSlideShow() {
      this.mainSlide = this.querySelector(".swiper-vely-hero-main");
      if (!this.mainSlide) return;

      const SwiperCtor = getSwiper();
      if (typeof SwiperCtor !== "function") return;

      if (this.mainSlide.swiper) {
        this._swiper = this.mainSlide.swiper;
        this.markReady();
        this.syncDock(this._swiper);
        this.refreshMobilePagination(this._swiper);
        this.syncVideoPlayButton(this._swiper);
        this.scheduleAutoAdvance(this._swiper);
        return;
      }

      const loop = this.mainSlide.dataset.loop === "true";
      const effect = this.mainSlide.dataset.effect || "slide";
      const pauseOnMouseEnter =
        this.mainSlide.dataset.pauseOnMouseEnter === "true";

      const _this = this;
      const dotsEl = this.querySelector(".swiper-pagination-vely-hero");
      const pagination = dotsEl
        ? {
            clickable: true,
            el: dotsEl,
          }
        : false;

      const base = {
        slidesPerView: 1,
        spaceBetween: 0,
        autoplay: false,
        effect: effect,
        loop: loop,
        speed: 400,
        watchSlidesProgress: true,
        pagination: pagination,
        on: {
          init: function (sw) {
            _this.querySelectorAll(".slide-video-1").forEach((video) => {
              _this.loadSlideVideo(video, false);
            });
            const revealInitialMotion = () => {
              const initialSlide = sw.slides[sw.activeIndex];
              if (!initialSlide) return false;
              const motions = initialSlide.querySelectorAll("motion-effect");
              if (!motions.length) return true;
              let allReady = true;
              motions.forEach((motion) => {
                if (typeof motion.initAnimateEffect !== "function") {
                  allReady = false;
                  return;
                }
                motion.classList.remove("animate-delay");
                Promise.resolve(motion.initAnimateEffect()).catch((e) => {
                  console.warn("[vely-hero-slideshow] motion-effect:", e);
                });
              });
              return allReady;
            };
            let revealAttempts = 0;
            const tryRevealInitial = () => {
              revealAttempts += 1;
              const ready = revealInitialMotion();
              if (!ready && revealAttempts < 45) {
                setTimeout(tryRevealInitial, 80);
                return;
              }
              if (!ready) {
                console.warn(
                  "[vely-hero-slideshow] motion-effect not upgraded in time; rely on CSS fallback for visible copy",
                );
              }
            };
            requestAnimationFrame(() => {
              tryRevealInitial();
              setTimeout(tryRevealInitial, 150);
            });
            if (window.customElements?.whenDefined) {
              customElements.whenDefined("motion-effect").then(() => {
                tryRevealInitial();
              });
            }
            _this.markReady();
            _this.syncDock(sw);
            _this.refreshMobilePagination(sw);
            _this.syncVideoPlayButton(sw);
            _this.scheduleAutoAdvance(sw);
          },
          slideChange: function () {
            _this.updateMobilePagination(this);
          },
          slideChangeTransitionStart: function () {
            const prevSlide = this.slides[this.previousIndex];
            if (prevSlide) {
              prevSlide.querySelectorAll("video").forEach((video) => {
                video.pause();
              });
            }
            const currentSlide = this.slides[this.activeIndex];
            if (!currentSlide) return;
            currentSlide
              .querySelectorAll("motion-effect")
              .forEach(async (motion) => {
                await motion.initAnimate();
              });
          },
          slideChangeTransitionEnd: function () {
            _this.querySelectorAll("video-local-slide").forEach((video) => {
              _this.loadSlideVideo(video, false);
            });
            const currentSlide = this.slides[this.activeIndex];
            if (!currentSlide) return;
            currentSlide
              .querySelectorAll("motion-effect")
              .forEach(async (motion) => {
                motion.classList.remove("animate-delay");
                await motion.initAnimateEffect();
              });
            _this.syncDock(this);
            _this.updateMobilePagination(this);
            requestAnimationFrame(() => {
              _this.syncVideoPlayButton(this);
              _this.scheduleAutoAdvance(this);
              window.setTimeout(() => {
                if (!this.destroyed) _this.syncVideoPlayButton(this);
              }, 60);
            });
          },
          realIndexChange: function () {
            _this.updateMobilePagination(this);
            requestAnimationFrame(() => {
              _this.syncVideoPlayButton(this);
            });
          },
        },
      };

      if (effect === "fade") {
        base.fadeEffect = { crossFade: true };
      }

      try {
        this._swiper = new SwiperCtor(this.mainSlide, base);
      } catch (error) {
        console.error("[vely-hero-slideshow] Swiper init failed:", error);
        return;
      }

      this.markReady();
      this.syncDock(this._swiper);
      this.refreshMobilePagination(this._swiper);
      this.syncVideoPlayButton(this._swiper);
      this.scheduleAutoAdvance(this._swiper);

      const sectionRoot = _this.closest(".section__slideshow");
      const autoplayOn = _this.isAutoChangeEnabled();
      if (sectionRoot && pauseOnMouseEnter && autoplayOn) {
        _this._onPointerEnter = () => {
          sectionRoot.classList.add("vely-hero-autoplay-paused");
          _this.pauseAutoAdvance();
        };
        _this._onPointerLeave = () => {
          sectionRoot.classList.remove("vely-hero-autoplay-paused");
          _this.resumeAutoAdvance();
        };
        _this.mainSlide.addEventListener("pointerenter", _this._onPointerEnter);
        _this.mainSlide.addEventListener("pointerleave", _this._onPointerLeave);
      }
    }

    markReady() {
      this.classList.add("vely-hero-slideshow--ready");
    }

    _onDockClick(event) {
      if (!this._swiper || this._swiper.destroyed) return;

      const prevBtn = event.target.closest(".vely-hero-slideshow__dock-prev");
      const nextBtn = event.target.closest(".vely-hero-slideshow__dock-next");
      const labelBtn = event.target.closest(".vely-hero-slideshow__nav-label");

      if (!prevBtn && !nextBtn && !labelBtn) return;

      event.preventDefault();
      event.stopPropagation();

      if (prevBtn) {
        this._swiper.slidePrev();
        return;
      }

      if (nextBtn) {
        this._swiper.slideNext();
        return;
      }

      if (labelBtn) {
        const idx = parseInt(labelBtn.dataset.slideIndex, 10);
        if (Number.isNaN(idx)) return;
        this.clearAutoAdvance();
        if (this._swiper.params.loop) {
          this._swiper.slideToLoop(idx);
        } else {
          this._swiper.slideTo(idx);
        }
      }
    }

    getSlideAtRealIndex(swiper, index) {
      const slides = this.getRealSlides(swiper);
      return slides[index] || null;
    }

    getSlideVideoFromSlide(slide) {
      if (!slide) return null;
      const isMobile = this.isMobileViewport();
      const hosts = slide.querySelectorAll("video-local-slide");
      for (const host of hosts) {
        if (isMobile && host.classList.contains("video-desktop")) continue;
        if (!isMobile && host.classList.contains("video-mobile")) continue;
        const video = host.querySelector("video");
        if (video) return video;
      }
      return slide.querySelector("video");
    }

    getSlideVideoAtIndex(swiper, index) {
      return this.getSlideVideoFromSlide(this.getSlideAtRealIndex(swiper, index));
    }

    getDefaultProgressSeconds() {
      const ms = this.mainSlide?.dataset.autoPlaySpeed
        ? Number(this.mainSlide.dataset.autoPlaySpeed)
        : 5000;
      return Number.isFinite(ms) && ms > 0 ? ms / 1000 : 5;
    }

    getSlideProgressDuration(swiper, index) {
      const fallbackSec = this.getDefaultProgressSeconds();
      if (this.getSlideTypeAtIndex(swiper, index) !== "video") {
        return fallbackSec;
      }
      const video = this.getSlideVideoAtIndex(swiper, index);
      if (
        video?.duration &&
        Number.isFinite(video.duration) &&
        video.duration > 0
      ) {
        return video.duration;
      }
      return fallbackSec;
    }

    isAutoChangeEnabled() {
      return this.mainSlide?.dataset.autoplay === "true";
    }

    isAdvancePaused(sectionRoot) {
      return (
        sectionRoot?.classList.contains("vely-hero-autoplay-paused") ||
        sectionRoot?.classList.contains("vely-hero-video-paused")
      );
    }

    clearAutoAdvance() {
      if (this._advanceTimer) {
        clearTimeout(this._advanceTimer);
        this._advanceTimer = null;
      }
      this._advanceStartedAt = null;
      this._advanceDurationMs = null;
      this._advanceRemainingMs = null;
    }

    pauseAutoAdvance() {
      if (!this._advanceTimer || this._advanceStartedAt == null) return;
      clearTimeout(this._advanceTimer);
      this._advanceTimer = null;
      const elapsed = Date.now() - this._advanceStartedAt;
      this._advanceRemainingMs = Math.max(
        0,
        (this._advanceDurationMs || 0) - elapsed,
      );
    }

    resumeAutoAdvance() {
      if (!this._swiper || this._swiper.destroyed) return;
      if (!this.isAutoChangeEnabled()) return;
      const sectionRoot = this.closest(".section__slideshow");
      if (this.isAdvancePaused(sectionRoot)) return;

      const remaining =
        this._advanceRemainingMs ??
        this.getSlideProgressDuration(this._swiper, this._swiper.realIndex) *
          1000;

      this._advanceStartedAt = Date.now();
      this._advanceDurationMs = remaining;
      this._advanceRemainingMs = null;
      this._advanceTimer = setTimeout(() => {
        this._advanceTimer = null;
        if (!this._swiper || this._swiper.destroyed) return;
        const section = this.closest(".section__slideshow");
        if (this.isAdvancePaused(section)) return;
        this._swiper.slideNext();
      }, remaining);
    }

    scheduleAutoAdvance(swiper) {
      this.clearAutoAdvance();
      if (!swiper || swiper.destroyed) return;
      if (this.getRealSlides(swiper).length <= 1) return;
      if (!this.isAutoChangeEnabled()) return;

      const sectionRoot = this.closest(".section__slideshow");
      if (this.isAdvancePaused(sectionRoot)) return;

      const realIdx = swiper.realIndex;
      const isVideo = this.getSlideTypeAtIndex(swiper, realIdx) === "video";
      const video = isVideo ? this.getSlideVideoAtIndex(swiper, realIdx) : null;
      if (
        isVideo &&
        video &&
        !(video.duration > 0 && Number.isFinite(video.duration))
      ) {
        const fallbackMs = this.getDefaultProgressSeconds() * 1000;
        this._advanceStartedAt = Date.now();
        this._advanceDurationMs = fallbackMs;
        this._advanceTimer = setTimeout(() => {
          this._advanceTimer = null;
          if (!this._swiper || this._swiper.destroyed) return;
          const section = this.closest(".section__slideshow");
          if (this.isAdvancePaused(section)) return;
          if (video.duration > 0 && Number.isFinite(video.duration)) {
            this.scheduleAutoAdvance(swiper);
            return;
          }
          this._swiper.slideNext();
        }, fallbackMs);
        video.addEventListener(
          "loadedmetadata",
          () => {
            if (swiper.destroyed || swiper.realIndex !== realIdx) return;
            this.scheduleAutoAdvance(swiper);
          },
          { once: true },
        );
        return;
      }

      const durationMs = this.getSlideProgressDuration(swiper, realIdx) * 1000;

      this._advanceStartedAt = Date.now();
      this._advanceDurationMs = durationMs;
      this._advanceTimer = setTimeout(() => {
        this._advanceTimer = null;
        if (!this._swiper || this._swiper.destroyed) return;
        const section = this.closest(".section__slideshow");
        if (this.isAdvancePaused(section)) return;
        this._swiper.slideNext();
      }, durationMs);
    }

    restartProgressFill(fill, durationSec) {
      fill.classList.remove("is-running");
      fill.style.removeProperty("animation");
      fill.style.removeProperty("transform");
      requestAnimationFrame(() => {
        fill.style.setProperty("--progress-duration", `${durationSec}s`);
        fill.classList.add("is-running");
      });
    }

    syncDock(swiper) {
      if (!swiper || swiper.destroyed) return;
      const realIdx = swiper.realIndex;
      const items = this.querySelectorAll(".vely-hero-slideshow__nav-label");

      items.forEach((btn, i) => {
        const active = i === realIdx;
        btn.classList.toggle("is-active", active);
        btn.setAttribute("aria-current", active ? "true" : "false");
        const fill = btn.querySelector(".vely-hero-slideshow__nav-progress-fill");
        if (!fill) return;
        if (active) {
          const durationSec = this.getSlideProgressDuration(swiper, i);
          const isVideo = this.getSlideTypeAtIndex(swiper, i) === "video";
          const video = isVideo ? this.getSlideVideoAtIndex(swiper, i) : null;
          const needsMetadata =
            isVideo &&
            video &&
            !(video.duration > 0 && Number.isFinite(video.duration));

          if (needsMetadata) {
            this.restartProgressFill(fill, durationSec);
            video.addEventListener(
              "loadedmetadata",
              () => {
                if (swiper.destroyed || swiper.realIndex !== i) return;
                const resolvedSec = this.getSlideProgressDuration(swiper, i);
                if (Math.abs(resolvedSec - durationSec) > 0.25) {
                  this.restartProgressFill(fill, resolvedSec);
                }
                this.scheduleAutoAdvance(swiper);
              },
              { once: true },
            );
          } else {
            this.restartProgressFill(fill, durationSec);
          }
        } else {
          fill.classList.remove("is-running");
          fill.style.removeProperty("animation");
          fill.style.removeProperty("--progress-duration");
          fill.style.transform = "scaleX(0)";
        }
      });
    }

    getRealSlides(swiper) {
      if (!swiper?.slides?.length) return [];
      return swiper.slides.filter(
        (slide) => !slide.classList.contains("swiper-slide-duplicate"),
      );
    }

    getSlideTypeAtIndex(swiper, index) {
      const slides = this.getRealSlides(swiper);
      return slides[index]?.dataset?.slideType || "";
    }

    getActiveSlideEl(swiper) {
      if (!swiper?.slides?.length) return null;
      return swiper.slides[swiper.activeIndex] || null;
    }

    getActiveSlideType(swiper) {
      const slide = this.getActiveSlideEl(swiper);
      if (slide?.dataset?.slideType) return slide.dataset.slideType;
      return this.getSlideTypeAtIndex(swiper, swiper?.realIndex ?? 0);
    }

    shouldShowMobilePlayButton(swiper) {
      if (!swiper || swiper.destroyed) return false;
      if (!this.isMobileViewport()) return false;
      if (this.getActiveSlideType(swiper) !== "video") return false;
      return Boolean(this.getActiveSlideVideo(swiper));
    }

    hideMobilePlayButton(sectionRoot) {
      if (!this._playBtn) return;
      sectionRoot?.classList.remove("vely-hero-video-paused");
      this._playBtn.classList.add("hidden");
      this._playBtn.setAttribute("hidden", "");
      this._playBtn.hidden = true;
    }

    showMobilePlayButton() {
      if (!this._playBtn) return;
      this._playBtn.classList.remove("hidden");
      this._playBtn.removeAttribute("hidden");
      this._playBtn.hidden = false;
    }

    getActiveSlideVideo(swiper) {
      if (!swiper?.slides?.length) return null;
      if (this.getActiveSlideType(swiper) !== "video") return null;
      const slide = swiper.slides[swiper.activeIndex];
      if (!slide) return null;

      const isMobile = this.isMobileViewport();
      const hosts = slide.querySelectorAll("video-local-slide");
      for (const host of hosts) {
        if (isMobile && host.classList.contains("video-desktop")) continue;
        if (!isMobile && host.classList.contains("video-mobile")) continue;
        const video = host.querySelector("video");
        if (video) return video;
      }

      return slide.querySelector("video");
    }

    refreshMobilePagination(swiper) {
      if (!swiper || swiper.destroyed) return;
      if (!this.isMobileViewport()) return;
      if (!this._paginationEl || !window.VelySwiperPagination) return;
      window.VelySwiperPagination.refresh(swiper, this._paginationEl);
      this.syncMobileBulletProgress(swiper);
    }

    updateMobilePagination(swiper) {
      if (!swiper || swiper.destroyed) return;
      if (!this.isMobileViewport()) return;
      if (!this._paginationEl || !window.VelySwiperPagination) return;
      window.VelySwiperPagination.setActiveBullet(swiper, this._paginationEl);
      this.syncMobileBulletProgress(swiper);
    }

    syncMobileBulletProgress(swiper) {
      if (!swiper || swiper.destroyed) return;
      if (!this._paginationEl) return;

      const bullets = this._paginationEl.querySelectorAll(
        ".swiper-pagination-bullet, .bullet",
      );
      const activeIdx = swiper.realIndex;

      bullets.forEach((bullet, i) => {
        bullet.classList.remove("is-progress-running");
        bullet.style.removeProperty("--progress-duration");
        if (i !== activeIdx) return;

        const durationSec = this.getSlideProgressDuration(swiper, i);
        bullet.style.setProperty("--progress-duration", `${durationSec}s`);
        requestAnimationFrame(() => {
          if (swiper.destroyed || swiper.realIndex !== i) return;
          bullet.classList.add("is-progress-running");
        });
      });
    }

    syncPlayButtonState(video) {
      if (!this._playBtn) return;
      const isPlaying = Boolean(video && !video.paused && !video.ended);
      this._playBtn.setAttribute("aria-pressed", isPlaying ? "true" : "false");
      const label = isPlaying
        ? this.getVideoPauseLabel()
        : this.getVideoPlayLabel();
      if (label) {
        this._playBtn.setAttribute("aria-label", label);
      }
    }

    syncVideoPlayButton(swiper) {
      const sectionRoot = this.closest(".section__slideshow");
      if (!this._playBtn) {
        sectionRoot?.classList.remove("vely-hero-video-paused");
        return;
      }

      if (!this.isMobileViewport()) {
        this.hideMobilePlayButton(sectionRoot);
        return;
      }

      if (!this.shouldShowMobilePlayButton(swiper)) {
        this.hideMobilePlayButton(sectionRoot);
        return;
      }

      this.showMobilePlayButton();
      this.syncPlayButtonState(this.getActiveSlideVideo(swiper));
    }

    _onPlayClick(event) {
      event.preventDefault();
      event.stopPropagation();
      if (!this._swiper || this._swiper.destroyed) return;

      const video = this.getActiveSlideVideo(this._swiper);
      if (!video) return;

      const btn = event.currentTarget;
      const sectionRoot = this.closest(".section__slideshow");
      if (video.paused || video.ended) {
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch((err) => {
            if (err?.name !== "AbortError") console.warn("play failed:", err);
          });
        }
        btn.setAttribute("aria-pressed", "true");
        const pauseLabel = this.getVideoPauseLabel();
        if (pauseLabel) btn.setAttribute("aria-label", pauseLabel);
        sectionRoot?.classList.remove("vely-hero-video-paused");
        this.resumeAutoAdvance();
        return;
      }

      video.pause();
      btn.setAttribute("aria-pressed", "false");
      const playLabel = this.getVideoPlayLabel();
      if (playLabel) btn.setAttribute("aria-label", playLabel);
      sectionRoot?.classList.add("vely-hero-video-paused");
      this.pauseAutoAdvance();
    }

    async loadSlideVideo(_this, lazy = false) {
      if (window.innerWidth > 787) {
        if (!lazy && _this.classList.contains("video-mobile")) {
          return;
        }
      } else {
        if (!lazy && _this.classList.contains("video-desktop")) {
          return;
        }
      }
      let video = _this.querySelector("video");

      if (
        !video &&
        !_this.getAttribute("loaded") &&
        _this.querySelector("template")
      ) {
        const content = document.createElement("div");
        content.appendChild(
          _this
            .querySelector("template")
            .content.firstElementChild.cloneNode(true),
        );
        _this.setAttribute("loaded", true);
        video = _this.appendChild(content.querySelector("video"));
      }

      if (!video || _this.dataset.videoInitialized === "true") {
        return;
      }

      _this.dataset.videoInitialized = "true";
      const media = _this.closest(".banner__media");

      if (!video.getAttribute("preload")) {
        video.preload = "metadata";
      }

      if (video.getAttribute("autoplay")) {
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch((err) => {
            if (err?.name !== "AbortError") console.warn("play failed:", err);
          });
        }
      }

      await this.waitForFirstFrame(video);
      _this.classList.add("is-ready");
      if (_this.dataset.firstSlideVideo === "true" && media) {
        media.classList.add("is-video-ready");
      }

      if (
        this._swiper &&
        !this._swiper.destroyed &&
        this.getActiveSlideVideo(this._swiper) === video
      ) {
        this.syncPlayButtonState(video);
      }

      
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach(({ target, isIntersecting, intersectionRatio }) => {
            if (isIntersecting && intersectionRatio > 0.2) {
              const p = video.play();
              if (p && typeof p.catch === "function") {
                p.catch((err) => {
                  if (err?.name !== "AbortError") console.warn("play failed:", err);
                });
              }
            } else {
              target.pause();
            }
          });
        },
        { threshold: [0, 0.2, 1] },
      );

      io.observe(video);
    }

    waitForFirstFrame(video) {
      return new Promise((resolve) => {
        const done = () => {
          cleanup();
          resolve();
        };
        const cleanup = () => {
          video.removeEventListener("loadeddata", done);
          video.removeEventListener("playing", done);
          video.removeEventListener("timeupdate", onTime);
        };
        const onTime = () => {
          if (video.currentTime > 0) done();
        };

        if (
          (video.readyState >= 2 && video.videoWidth) ||
          video.currentTime > 0
        ) {
          return resolve();
        }
        video.addEventListener("loadeddata", done, { once: true });
        video.addEventListener("playing", done, { once: true });
        video.addEventListener("timeupdate", onTime, { once: true });

        if ("requestVideoFrameCallback" in video) {
          video.requestVideoFrameCallback(() => {
            done();
          });
        }
      });
    }
  }

  if (!customElements.get("vely-hero-slideshow")) {
    customElements.define("vely-hero-slideshow", VelyHeroSlideshow);
  }
})();

/** @velyn-custom END — vely-hero-slideshow.js */
